import HttpService from "./http-service";
import Statistics from "@/entities/statistics";

export default class StatisticsService extends HttpService {
  public async getGeneralStatistics(): Promise<Statistics> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { data } = await this.httpClient.get("general-statistics");
    return data;
  }

  public getExportLink(
    name: string,
    month: number,
    year: number,
    format: string
  ): string {
    return `${
      this.httpClient.defaults.baseURL ?? ""
    }/public-statistics-export?period_month=${month}&period_year=${year}&name=${name}&format=${format}`;
  }

  public async getPublicRequestsStatistics(month: number, year: number) {
    const { data } = await this.httpClient.get(
      `public-requests-statistics?period_month=${month}&period_year=${year}`
    );

    if (!data) {
      return {};
    }

    return {
      requestsPerStateType: this.formatRequestsPerStateType(
        data.total_requests_by_type_and_status || []
      ),
      meanRequestScorePerEntity: this.formatMeanRequestScorePerEntity(
        data.entities_requests_score_average_top || []
      ),
      solicitantPerGender: this.formatSolicitantPerGender(
        data.citizen_requests_total_by_gender || [],
        { 1: "male", 2: "female", null: "unknown" },
        { male: 0, female: 0, unknown: 0 }
      ),
      solicitantWithDisabilityPerGender: this.formatSolicitantPerGender(
        data.citizen_with_disability_requests_total_by_gender || [],
        { 1: "male", 2: "female" },
        { male: 0, female: 0 }
      ),
      solicitantPerEthnicity: this.formatSolicitantPerEthnicity(
        data.citizen_requests_total_by_ethnic_group || []
      ),
      solicitantPerProvince: this.formatSolicitantPerProvince(
        data.citizen_requests_total_by_province || []
      ),
    };
  }

  public async getPublicComplianceStatistics(month: number, year: number) {
    const { data } = await this.httpClient.get(
      `public-compliance-statistics?period_month=${month}&period_year=${year}`
    );

    if (!data) {
      return {};
    }

    return {
      compliantVsNonCompliantInstitutions: this.formatCompliantVsNonCompliant(
        1,
        data.entities_with_full_compliance_percentage_by_type || {}
      ),
      compliantVsNonCompliantMunicipalities: this.formatCompliantVsNonCompliant(
        2,
        data.entities_with_full_compliance_percentage_by_type || {}
      ),
      compliantVsNonCompliantCommunalBoards: this.formatCompliantVsNonCompliant(
        3,
        data.entities_with_full_compliance_percentage_by_type || {}
      ),
      compliancePercentagePerInstitutions: this.formatCompliancePercentage(
        1,
        data.entities_with_compliance_percentage_per_type || {}
      ),
      compliancePercentagePerMunicipalities: this.formatCompliancePercentage(
        2,
        data.entities_with_compliance_percentage_per_type || {}
      ),

      compliancePercentagePerCommunalBoards: this.formatCompliancePercentage(
          3,
          data.entities_with_compliance_percentage_per_type || {}
      ),
    };
  }

  getRequestCountByStatusAndType(
    requestsPerStateType: Array<any>,
    statusId: number,
    typeId: number
  ): number {
    const request = requestsPerStateType.find(
      (request) =>
        request.status_id == statusId && request.type_request_id == typeId
    );

    return request?.total || 0;
  }

  formatRequestsPerStateType(requestsPerStateType: Array<any>): any {
    if (!requestsPerStateType || !requestsPerStateType.length) {
      return {};
    }

    const status = [
      {
        id: 3,
        name: "Resueltas",
      },
      {
        id: 4,
        name: "Negadas",
      },
      {
        id: 2,
        name: "En trámite",
      },
    ];

    const types = [
      {
        id: 1,
        name: "Derecho de petición",
      },
      {
        id: 2,
        name: "Derecho de acceso a la información",
      },
    ];

    const perType: any = [];

    types.forEach((type) => {
      const counts: any = [];

      status.forEach((status) => {
        counts.push(
          this.getRequestCountByStatusAndType(
            requestsPerStateType,
            status.id,
            type.id
          )
        );
      });

      perType.push({ counts, type: type.name });
    });

    return {
      states: status.map((status) => status.name),
      perType,
    };
  }

  formatMeanRequestScorePerEntity(entitiesWithScore: Array<any>): any {
    const entities: Array<string> = [];
    const means: Array<number> = [];

    entitiesWithScore.forEach((entity) => {
      entities.push(entity.acronym);
      means.push(Number(entity.total));
    });

    return {
      entities,
      means,
    };
  }

  formatSolicitantPerGender(
    requests: Array<any>,
    genderIds: any,
    defaultResult: any = {}
  ): any {
    const result: any = defaultResult;

    requests.forEach((request) => {
      const genderIdKey = genderIds[request.gender_id];

      if (genderIdKey) {
        result[genderIdKey] = request.total;
      }
    });

    return result;
  }

  formatSolicitantPerEthnicity(requests: Array<any>): any {
    const ethnicities: Array<string> = [];
    const counts: Array<number> = [];

    requests.forEach((request) => {
      ethnicities.push(request.name || "N/A");
      counts.push(Number(request.total));
    });

    return {
      ethnicities,
      counts,
    };
  }

  formatSolicitantPerProvince(requests: Array<any>): any {
    const provinces: Array<string> = [];
    const counts: Array<number> = [];

    requests.forEach((request) => {
      provinces.push(request.name);
      counts.push(Number(request.total));
    });

    return {
      provinces,
      counts,
    };
  }

  formatCompliantVsNonCompliant(typeId: number, compliance: any): any {
    const entityCompliance = compliance[typeId];

    if (!entityCompliance) {
      return { compliant: 0, nonCompliant: 0 };
    }

    return {
      compliant: compliance[typeId]?.percentage_with_full_compliance,
      nonCompliant: compliance[typeId]?.percentage_without_full_compliance,
    };
  }

  formatCompliancePercentage(typeId: number, compliance: any): any {
    const entityCompliance = compliance[typeId];

    if (!entityCompliance || !entityCompliance.length) {
      return { entities: [], annual: [], monthly: [] };
    }

    const entities: Array<string> = [];
    const annual: Array<number> = [];
    const monthly: Array<number> = [];

    entityCompliance.forEach((entity: any) => {
      entities.push(entity.acronym);
      annual.push(Number(entity.annual));
      monthly.push(Number(entity.monthly));
    });

    return {
      entities,
      annual,
      monthly,
    };
  }
}
